import { api } from '../../../shared/lib/axios';
import type { Company, CompanyBankAccount } from '../../../shared/lib/api';

export const EMPRESAS_KEYS = {
  all: ['empresas'] as const,
  list: (params?: object) => ['empresas', 'list', params] as const,
};

interface ListParams {
  cnpj?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}

interface ListResponse {
  data: Company[];
  pagination?: { limit: number; offset: number; count: number };
}

export async function listarEmpresas(params?: ListParams): Promise<ListResponse> {
  const query = new URLSearchParams();
  if (params?.cnpj) query.set('cnpj', params.cnpj);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  const { data } = await api.get<ListResponse>(`/companies${qs ? `?${qs}` : ''}`);
  return data;
}

export interface CreateCompanyPayload {
  corporateName: string;
  tradeName: string;
  cnpj: string;
  responsibleName: string;
  responsibleEmail: string;
  responsiblePhone: string;
}

export async function buscarEmpresa(id: string): Promise<Company> {
  const { data } = await api.get<Company>(`/companies/${id}`);
  return data;
}

export async function criarEmpresa(payload: CreateCompanyPayload): Promise<Company> {
  const { data } = await api.post<Company>('/companies', payload);
  return data;
}

export interface UpdateCompanyPayload {
  corporateName?: string;
  tradeName?: string;
  cnpj?: string;
  responsibleName?: string;
  responsibleEmail?: string;
  responsiblePhone?: string;
}

export async function atualizarEmpresa(id: string, payload: UpdateCompanyPayload): Promise<Company> {
  const { data } = await api.patch<Company>(`/companies/${id}`, payload);
  return data;
}

export async function deletarEmpresa(id: string): Promise<void> {
  await api.delete(`/companies/${id}`);
}

export async function reativarEmpresa(id: string): Promise<Company> {
  const { data } = await api.patch<Company>(`/companies/${id}`, { isActive: true });
  return data;
}

export async function uploadLogo(companyId: string, file: File): Promise<Company> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<Company>(`/companies/${companyId}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export const BANK_ACCOUNTS_KEYS = {
  all: (companyId: string) => ['empresas', companyId, 'bank-accounts'] as const,
};

export interface BankAccountPayload {
  bankName: string;
  bankAgency: string;
  bankAccount: string;
  accountType: string;
  pixKey?: string;
}

export async function listarContasBancarias(companyId: string): Promise<CompanyBankAccount[]> {
  const { data } = await api.get<{ data: CompanyBankAccount[] }>(`/companies/${companyId}/bank-accounts`);
  return data.data;
}

export async function criarContaBancaria(companyId: string, payload: BankAccountPayload): Promise<CompanyBankAccount> {
  const { data } = await api.post<CompanyBankAccount>(`/companies/${companyId}/bank-accounts`, payload);
  return data;
}

export async function atualizarContaBancaria(companyId: string, bankId: string, payload: Partial<BankAccountPayload>): Promise<CompanyBankAccount> {
  const { data } = await api.patch<CompanyBankAccount>(`/companies/${companyId}/bank-accounts/${bankId}`, payload);
  return data;
}

export async function deletarContaBancaria(companyId: string, bankId: string): Promise<void> {
  await api.delete(`/companies/${companyId}/bank-accounts/${bankId}`);
}
